import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, Mail, Phone, Calendar, MapPin, Code, Receipt, StickyNote, Car, Home, ExternalLink, X, Maximize2, Bell, MessageCircle, Send, Search, AlertCircle, Flag, CheckCircle, RefreshCw, Clock, Copy, Download, BarChart3, Sheet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { downloadExcel } from "@/lib/downloadExcel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_SIZE = 25;
const STATUSES = ["Pending Review", "Missing Information", "Info Verified", "Scheduled", "Completed", "Denied"];
const PRIORITIES = ["Urgent", "Normal"];

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortByDate, setSortByDate] = useState("newest"); // "newest" or "oldest"
  const [filterPriority, setFilterPriority] = useState("all");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Pending Review"); // Active status tab
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [updatingClaim, setUpdatingClaim] = useState(null);
  const [viewModal, setViewModal] = useState({
    isOpen: false,
    type: "", // "image" or "invoice"
    filename: "",
    title: "",
    claimName: ""
  });
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: "",
    imageAlt: ""
  });
  const [messageModal, setMessageModal] = useState({
    isOpen: false,
    claim: null,
    messages: [],
    newMessage: "",
    isLoading: false,
    isSending: false
  });
  
  // State for inline messages in tabs
  const [claimMessages, setClaimMessages] = useState({}); // { claimId: { messages: [], newMessage: "", isLoading: false, isSending: false } }
  
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    claimId: null,
    newStatus: null,
    missingInfoMessage: "",
    scheduledDate: "",
    scheduledTime: "",
    denialReason: ""
  });

  const [rescheduleModal, setRescheduleModal] = useState({
    isOpen: false,
    claimId: null,
    claimNumber: null,
    currentDate: "",
    currentTime: "",
    newDate: "",
    newTime: "",
  });

  useEffect(() => {
    fetchClaims();
    axios.get(`${API}/claims/sheet/info`).then((r) => setSheetInfo(r.data)).catch(() => {});
  }, []);

  const [sheetInfo, setSheetInfo] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const resyncSheet = async () => {
    setResyncing(true);
    try {
      const r = await axios.post(`${API}/claims/sheet/resync`);
      if (!r.data.started) { toast.info(r.data.message); return; }
      toast.success(`Re-sync started for ${r.data.claims} claims — the sheet will update within a minute`);
      const poll = setInterval(async () => {
        const info = (await axios.get(`${API}/claims/sheet/info`)).data;
        setSheetInfo(info);
        if (!info.running) {
          clearInterval(poll);
          setResyncing(false);
          if (info.last_error) toast.error(`Sheet re-sync failed: ${info.last_error}`);
          else toast.success("Google Sheet re-sync complete");
        }
      }, 3000);
    } catch (e) {
      setResyncing(false);
      toast.error(e.response?.data?.detail || "Failed to start re-sync");
    }
  };

  // Deep link from Analytics: /dashboard?claim=12345 opens that claim's tab and filters to it
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkClaim = searchParams.get("claim");
  useEffect(() => {
    if (!deepLinkClaim || claims.length === 0) return;
    const target = claims.find((c) => String(c.claim_number) === String(deepLinkClaim));
    if (target) {
      setActiveTab(target.status || "Pending Review");
      setSearchQuery(String(deepLinkClaim));
    } else {
      toast.error(`Claim #${deepLinkClaim} not found`);
    }
    setSearchParams({}, { replace: true }); // apply once, then drop the param so Refresh doesn't re-apply it
  }, [deepLinkClaim, claims]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab, searchQuery, filterPriority, sortByDate]);

  const fetchClaims = async () => {
    setLoading(true);
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await axios.get(`${API}/claims`, { timeout: 30000 });
        setClaims(response.data);
        setLoading(false);
        return;
      } catch (error) {
        console.error(`Error fetching claims (attempt ${attempt}):`, error);
        if (attempt < 3) await new Promise((r) => setTimeout(r, 2000 * attempt));
      }
    }
    setLoading(false);
    toast.error("Failed to load claims — please tap Refresh to try again");
  };

  const updateClaimStatus = async (claimId, newStatus) => {
    // Check if status requires additional input
    if (newStatus === "Missing Information" || newStatus === "Scheduled" || newStatus === "Denied") {
      setStatusModal({
        isOpen: true,
        claimId,
        newStatus,
        missingInfoMessage: "",
        scheduledDate: "",
        scheduledTime: "",
        denialReason: ""
      });
      return;
    }
    
    // For statuses that don't require additional input, update directly
    try {
      setUpdatingClaim(claimId);
      await axios.patch(`${API}/claims/${claimId}/status`, {
        status: newStatus,
      });
      toast.success(`Status updated to "${newStatus}"`);
      fetchClaims();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    } finally {
      setUpdatingClaim(null);
    }
  };

  const submitStatusUpdate = async () => {
    const { claimId, newStatus, missingInfoMessage, scheduledDate, scheduledTime, denialReason } = statusModal;
    
    try {
      setUpdatingClaim(claimId);
      
      const payload = { status: newStatus };
      
      if (newStatus === "Missing Information") {
        if (!missingInfoMessage.trim()) {
          toast.error("Please enter what information is missing");
          return;
        }
        payload.missing_info_message = missingInfoMessage;
      } else if (newStatus === "Scheduled") {
        if (!scheduledDate) {
          toast.error("Please enter scheduled date");
          return;
        }
        payload.scheduled_date = scheduledDate;
        if (scheduledTime) {
          payload.scheduled_time = scheduledTime;
        }
      } else if (newStatus === "Denied") {
        if (!denialReason.trim()) {
          toast.error("Please enter denial reason");
          return;
        }
        payload.denial_reason = denialReason;
      }
      
      await axios.patch(`${API}/claims/${claimId}/status`, payload);
      
      toast.success(`Status updated to "${newStatus}"`);
      
      // Clear message cache so Messages tab reloads with the new SMS log
      setClaimMessages(prev => {
        const updated = { ...prev };
        delete updated[claimId];
        return updated;
      });
      
      // Close modal and refresh
      setStatusModal({ isOpen: false, claimId: null, newStatus: null, missingInfoMessage: "", scheduledDate: "", scheduledTime: "", denialReason: "" });
      fetchClaims();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingClaim(null);
    }
  };
  
  const closeStatusModal = () => {
    setStatusModal({ isOpen: false, claimId: null, newStatus: null, missingInfoMessage: "", scheduledDate: "", scheduledTime: "", denialReason: "" });
  };

  const openReschedule = (claim) => {
    setRescheduleModal({
      isOpen: true,
      claimId: claim.id,
      claimNumber: claim.claim_number,
      currentDate: claim.scheduled_date || "",
      currentTime: claim.scheduled_time || "",
      newDate: claim.scheduled_date || "",
      newTime: claim.scheduled_time || "",
    });
  };

  const submitReschedule = async () => {
    if (!rescheduleModal.newDate) {
      toast.error("Please select a new date");
      return;
    }
    try {
      setUpdatingClaim(rescheduleModal.claimId);
      await axios.patch(`${API}/claims/${rescheduleModal.claimId}/reschedule`, {
        scheduled_date: rescheduleModal.newDate,
        scheduled_time: rescheduleModal.newTime || null,
      });
      toast.success("Appointment rescheduled!");
      setRescheduleModal({ isOpen: false, claimId: null, claimNumber: null, currentDate: "", currentTime: "", newDate: "", newTime: "" });
      // Clear message cache so new reschedule log shows
      setClaimMessages(prev => {
        const updated = { ...prev };
        delete updated[rescheduleModal.claimId];
        return updated;
      });
      fetchClaims();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reschedule");
    } finally {
      setUpdatingClaim(null);
    }
  };

  const updateClaimPriority = async (claimId, newPriority) => {
    try {
      setUpdatingClaim(claimId);
      await axios.patch(`${API}/claims/${claimId}/priority`, {
        priority: newPriority,
      });
      toast.success(`Priority updated to "${newPriority}"`);
      fetchClaims();
    } catch (error) {
      console.error("Error updating priority:", error);
      toast.error("Failed to update priority");
    } finally {
      setUpdatingClaim(null);
    }
  };

  const handleViewFile = (filename, type, claimName) => {
    setViewModal({
      isOpen: true,
      type: type,
      filename: filename,
      title: type === "image" ? "Diagnostic Code Image" : "Invoice Document",
      claimName: claimName
    });
  };

  const copyClaimInfo = (claim) => {
    const vehicle = [claim.vehicle_year, claim.vehicle_make, claim.vehicle_model].filter(Boolean).join(" ");
    const address = claim.street_address 
      ? `${claim.street_address}${claim.apartment_unit ? `, ${claim.apartment_unit}` : ""}`
      : "";
    const cityStateZip = `${claim.city || ""}, ${claim.state || ""} ${claim.zip_code || ""}`.trim();
    
    const lines = [
      `Name: ${claim.customer_name}`,
      `Phone: ${claim.customer_phone}`,
      `Email: ${claim.customer_email}`,
      vehicle ? `Vehicle: ${vehicle}` : "",
      address ? `Address: ${address}` : "",
      cityStateZip ? `City/State/Zip: ${cityStateZip}` : "",
      claim.claim_number ? `Claim #: ${claim.claim_number}` : "",
    ].filter(Boolean).join("\n");

    const textArea = document.createElement("textarea");
    textArea.value = lines;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    toast.success("Copied!");
  };

  const closeModal = () => {
    setViewModal({
      isOpen: false,
      type: "",
      filename: "",
      title: "",
      claimName: ""
    });
  };

  const openInNewTab = () => {
    window.open(`${BACKEND_URL}/api/uploads/${viewModal.filename}`, '_blank');
  };

  const openMessageModal = async (claim) => {
    setMessageModal({
      isOpen: true,
      claim: claim,
      messages: [],
      newMessage: "",
      isLoading: true,
      isSending: false
    });

    try {
      const response = await axios.get(`${API}/claims/${claim.id}/messages`);
      setMessageModal(prev => ({
        ...prev,
        messages: response.data,
        isLoading: false
      }));

      // Mark messages as read and clear the badge locally
      await axios.patch(`${API}/claims/${claim.id}/messages/read`);
      setClaims(prev => prev.map(c => c.id === claim.id ? { ...c, unread_messages_count: 0 } : c));
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessageModal(prev => ({
        ...prev,
        isLoading: false
      }));
      toast.error("Failed to load messages");
    }
  };

  const closeMessageModal = () => {
    setMessageModal({
      isOpen: false,
      claim: null,
      messages: [],
      newMessage: "",
      isLoading: false,
      isSending: false
    });
  };

  const sendMessage = async () => {
    if (!messageModal.newMessage.trim() || messageModal.isSending) return;

    setMessageModal(prev => ({ ...prev, isSending: true }));

    try {
      const response = await axios.post(`${API}/claims/${messageModal.claim.id}/messages`, {
        message: messageModal.newMessage.trim()
      });

      // Add new message to the list
      setMessageModal(prev => ({
        ...prev,
        messages: [...prev.messages, response.data],
        newMessage: "",
        isSending: false
      }));

      toast.success("Message sent to customer");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
      setMessageModal(prev => ({ ...prev, isSending: false }));
    }
  };
  
  // Inline message functions
  const loadClaimMessages = async (claimId, forceRefresh = false) => {
    if (claimMessages[claimId] && !forceRefresh) return; // Already loaded
    
    setClaimMessages(prev => ({
      ...prev,
      [claimId]: { messages: [], newMessage: "", isLoading: true, isSending: false }
    }));

    try {
      const response = await axios.get(`${API}/claims/${claimId}/messages`);
      setClaimMessages(prev => ({
        ...prev,
        [claimId]: { ...prev[claimId], messages: response.data, isLoading: false }
      }));

      // Mark messages as read and clear the badge locally (no full list refetch)
      await axios.patch(`${API}/claims/${claimId}/messages/read`);
      setClaims(prev => prev.map(c => c.id === claimId ? { ...c, unread_messages_count: 0 } : c));
    } catch (error) {
      console.error("Error fetching messages:", error);
      setClaimMessages(prev => ({
        ...prev,
        [claimId]: { ...prev[claimId], isLoading: false }
      }));
      toast.error("Failed to load messages");
    }
  };
  
  const sendInlineMessage = async (claimId) => {
    const claimMsgData = claimMessages[claimId];
    if (!claimMsgData?.newMessage?.trim() || claimMsgData.isSending) return;

    setClaimMessages(prev => ({
      ...prev,
      [claimId]: { ...prev[claimId], isSending: true }
    }));

    try {
      const response = await axios.post(`${API}/claims/${claimId}/messages`, {
        message: claimMsgData.newMessage.trim()
      });

      // Add new message to the list
      setClaimMessages(prev => ({
        ...prev,
        [claimId]: {
          ...prev[claimId],
          messages: [...prev[claimId].messages, response.data],
          newMessage: "",
          isSending: false
        }
      }));

      toast.success("Message sent to customer");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
      setClaimMessages(prev => ({
        ...prev,
        [claimId]: { ...prev[claimId], isSending: false }
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending Review":
        return "status-pending";
      case "Info Verified":
        return "status-verified";
      case "Scheduled":
        return "status-scheduled";
      case "Completed":
        return "status-completed";
      default:
        return "status-pending";
    }
  };

  // Count claims by status
  const pendingReviewCount = claims.filter(c => c.status === "Pending Review").length;
  const missingInfoCount = claims.filter(c => c.status === "Missing Information").length;
  const infoVerifiedCount = claims.filter(c => c.status === "Info Verified").length;
  const scheduledCount = claims.filter(c => c.status === "Scheduled").length;
  const completedCount = claims.filter(c => c.status === "Completed").length;
  const deniedCount = claims.filter(c => c.status === "Denied").length;

  const filteredClaims = claims
    .filter((claim) => {
      // Filter by active tab (status)
      if (claim.status !== activeTab) {
        return false;
      }
      
      // Filter by priority
      if (filterPriority !== "all" && claim.priority !== filterPriority) {
        return false;
      }
      
      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = claim.customer_name?.toLowerCase().includes(query);
        const matchesClaimNumber = claim.claim_number?.toString().includes(query);
        const matchesPhone = claim.customer_phone?.includes(query);
        
        if (!matchesName && !matchesClaimNumber && !matchesPhone) {
          return false;
        }
      }
      
      return true;
    })
    .sort((a, b) => {
      // Only sort by priority first if a specific priority filter is selected
      if (filterPriority !== "all") {
        const priorityOrder = { "Urgent": 0, "Normal": 1 };
        const priorityA = priorityOrder[a.priority] ?? 1;
        const priorityB = priorityOrder[b.priority] ?? 1;
        
        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }
      }
      
      // Sort by date
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      
      return sortByDate === "newest" ? dateB - dateA : dateA - dateB;
    });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatScheduleDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "long", day: "numeric", year: "numeric" });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Urgent":
        return "text-red-600 bg-red-50 border-red-200";
      case "Normal":
        return "text-blue-600 bg-blue-50 border-blue-200";
      default:
        return "text-blue-600 bg-blue-50 border-blue-200";
    }
  };

  const getPriorityIcon = (priority) => {
    if (priority === "Urgent") {
      return <Flag className="w-4 h-4" />;
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-gray-900" data-testid="dashboard-title">
              Warranty Claims Manager
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage and track all warranty claims
            </p>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {sheetInfo?.configured && (
              <>
                <a href={sheetInfo.url} target="_blank" rel="noreferrer" data-testid="open-sheet-link">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Sheet className="w-4 h-4 text-emerald-600" />
                    Open Sheet
                  </Button>
                </a>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={resyncing}
                  onClick={resyncSheet}
                  data-testid="resync-sheet-button"
                >
                  <RefreshCw className={`w-4 h-4 ${resyncing ? "animate-spin" : ""}`} />
                  {resyncing ? "Re-syncing…" : "Re-sync Sheet"}
                </Button>
              </>
            )}
            <Button
              onClick={() => navigate("/analytics")}
              size="sm"
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="analytics-button"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </Button>
            <Button
              onClick={() => navigate("/h2g")}
              variant="outline"
              size="sm"
              className="gap-2"
              data-testid="back-to-home-button"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-3 sm:p-6">
        {/* Search and Filter Section */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, claim #, or phone number..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={() => downloadExcel(`${API}/claims/export`, "Hybrid2Go_Claims.xlsx")}
                variant="outline"
                className="px-4 py-2 rounded-lg flex items-center gap-2 whitespace-nowrap"
                data-testid="export-claims-button"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </Button>
              <Button
                onClick={() => {
                  fetchClaims();
                  toast.success("Claims refreshed!");
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 whitespace-nowrap"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>

            {/* Filters Row */}
            <div className="flex flex-wrap gap-4 items-center">

              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Priority:</span>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                  <SelectTrigger className="w-[150px]" data-testid="priority-filter">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    {PRIORITIES.map((priority) => (
                      <SelectItem key={priority} value={priority}>
                        {priority}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-700">Sort:</span>
                <Select value={sortByDate} onValueChange={setSortByDate}>
                  <SelectTrigger className="w-[150px]" data-testid="date-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="ml-auto text-sm text-gray-600">
                Showing <span className="font-semibold">{filteredClaims.length}</span> claim
                {filteredClaims.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="mb-4 sm:mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-0">
            <button
              onClick={() => setActiveTab("Pending Review")}
              data-testid="status-tab-pending-review"
              className={`px-2 sm:px-3 py-3 font-medium transition-colors text-[10px] sm:text-sm border-b-2 ${
                activeTab === "Pending Review"
                  ? "bg-orange-50 text-orange-700 border-orange-500"
                  : "text-gray-600 hover:bg-gray-50 border-transparent"
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-center">Pending Review</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === "Pending Review" 
                    ? "bg-orange-500 text-white" 
                    : "bg-gray-200 text-gray-700"
                }`}>
                  {pendingReviewCount}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab("Missing Information")}
              data-testid="status-tab-missing-information"
              className={`px-3 py-3 font-medium transition-colors text-xs sm:text-sm border-b-2 ${
                activeTab === "Missing Information"
                  ? "bg-yellow-50 text-yellow-700 border-yellow-500"
                  : "text-gray-600 hover:bg-gray-50 border-transparent"
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-center">Missing Info</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === "Missing Information" 
                    ? "bg-yellow-500 text-white" 
                    : "bg-gray-200 text-gray-700"
                }`}>
                  {missingInfoCount}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab("Info Verified")}
              data-testid="status-tab-info-verified"
              className={`px-3 py-3 font-medium transition-colors text-xs sm:text-sm border-b-2 ${
                activeTab === "Info Verified"
                  ? "bg-blue-50 text-blue-700 border-blue-500"
                  : "text-gray-600 hover:bg-gray-50 border-transparent"
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-center">Info Verified</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === "Info Verified" 
                    ? "bg-blue-500 text-white" 
                    : "bg-gray-200 text-gray-700"
                }`}>
                  {infoVerifiedCount}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab("Scheduled")}
              data-testid="status-tab-scheduled"
              className={`px-3 py-3 font-medium transition-colors text-xs sm:text-sm border-b-2 ${
                activeTab === "Scheduled"
                  ? "bg-purple-50 text-purple-700 border-purple-500"
                  : "text-gray-600 hover:bg-gray-50 border-transparent"
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-center">Scheduled</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === "Scheduled" 
                    ? "bg-purple-500 text-white" 
                    : "bg-gray-200 text-gray-700"
                }`}>
                  {scheduledCount}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab("Completed")}
              data-testid="status-tab-completed"
              className={`px-3 py-3 font-medium transition-colors text-xs sm:text-sm border-b-2 ${
                activeTab === "Completed"
                  ? "bg-green-50 text-green-700 border-green-500"
                  : "text-gray-600 hover:bg-gray-50 border-transparent"
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-center">Completed</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === "Completed" 
                    ? "bg-green-500 text-white" 
                    : "bg-gray-200 text-gray-700"
                }`}>
                  {completedCount}
                </span>
              </div>
            </button>
            
            <button
              onClick={() => setActiveTab("Denied")}
              data-testid="status-tab-denied"
              className={`px-3 py-3 font-medium transition-colors text-xs sm:text-sm border-b-2 ${
                activeTab === "Denied"
                  ? "bg-red-50 text-red-700 border-red-500"
                  : "text-gray-600 hover:bg-gray-50 border-transparent"
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <span className="text-center">Denied</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === "Denied" 
                    ? "bg-red-500 text-white" 
                    : "bg-gray-200 text-gray-700"
                }`}>
                  {deniedCount}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Claims List */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
          </div>
        ) : filteredClaims.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center" data-testid="no-claims-message">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No claims found
            </h3>
            <p className="text-gray-500">
              No claims with status "{activeTab}" found.
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredClaims.slice(0, visibleCount).map((claim) => (
              <div key={claim.id} className="claim-card" data-testid={`claim-card-${claim.id}`}>
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-between items-start pb-4 border-b border-gray-200">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                        {claim.customer_name}
                      </h3>
                      {claim.claim_number && (
                        <span className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-md text-xs sm:text-sm font-mono font-semibold">
                          #{claim.claim_number}
                        </span>
                      )}
                      <button
                        onClick={() => copyClaimInfo(claim)}
                        className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md text-xs font-medium transition-colors flex items-center gap-1"
                        data-testid={`copy-claim-${claim.id}`}
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                      {/* Unread messages indicator */}
                      {claim.unread_messages_count > 0 && (
                        <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs font-bold flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {claim.unread_messages_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(claim.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Priority Badge */}
                    <div className={`flex items-center gap-1 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-semibold border ${getPriorityColor(claim.priority || 'Normal')}`}>
                      {getPriorityIcon(claim.priority || 'Normal')}
                      <span>{claim.priority || 'Normal'}</span>
                    </div>
                    <span className={`status-badge ${getStatusColor(claim.status)} text-xs whitespace-nowrap`} data-testid={`claim-status-${claim.id}`}>
                      {claim.status}
                      {claim.status === "Scheduled" && claim.scheduled_date && new Date(claim.scheduled_date + 'T23:59:59') < new Date() && (
                        <span className="ml-2">🚩</span>
                      )}
                    </span>
                  </div>
                </div>

                <Tabs 
                  defaultValue="details" 
                  className="w-full"
                  onValueChange={(value) => {
                    // Refresh messages when switching to Messages tab
                    if (value === "messages") {
                      loadClaimMessages(claim.id);
                    }
                  }}
                >
                  <TabsList className="grid w-full grid-cols-2 h-11 sm:h-10">
                    <TabsTrigger value="details" className="h-9 sm:h-8">Claim Details</TabsTrigger>
                    <TabsTrigger value="messages" className="flex items-center gap-2 h-9 sm:h-8">
                      <MessageCircle className="w-4 h-4" />
                      Messages
                      {claim.unread_messages_count > 0 && (
                        <span className="bg-red-500 text-white rounded-full text-xs px-1.5 py-0.5 min-w-[20px] h-5 flex items-center justify-center">
                          {claim.unread_messages_count}
                        </span>
                      )}
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="details" className="mt-6">
                    {/* Scheduled Appointment Banner */}
                    {claim.status === "Scheduled" && claim.scheduled_date && (
                      <div className={`mb-6 p-4 rounded-xl border-2 flex items-center justify-between ${
                        new Date(claim.scheduled_date + 'T23:59:59') < new Date()
                          ? "bg-red-50 border-red-300"
                          : "bg-violet-50 border-violet-200"
                      }`} data-testid={`schedule-banner-${claim.id}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            new Date(claim.scheduled_date + 'T23:59:59') < new Date()
                              ? "bg-red-100"
                              : "bg-violet-100"
                          }`}>
                            <Clock className={`w-5 h-5 ${
                              new Date(claim.scheduled_date + 'T23:59:59') < new Date() ? "text-red-600" : "text-violet-600"
                            }`} />
                          </div>
                          <div>
                            <p className={`text-sm font-bold ${
                              new Date(claim.scheduled_date + 'T23:59:59') < new Date() ? "text-red-800" : "text-violet-800"
                            }`}>
                              {new Date(claim.scheduled_date + 'T23:59:59') < new Date() ? "PAST DUE" : "Scheduled"}
                            </p>
                            <p className={`text-base font-semibold ${
                              new Date(claim.scheduled_date + 'T23:59:59') < new Date() ? "text-red-700" : "text-violet-700"
                            }`}>
                              {formatScheduleDate(claim.scheduled_date)}
                              {claim.scheduled_time && ` at ${claim.scheduled_time}`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => openReschedule(claim)}
                          className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                            new Date(claim.scheduled_date + 'T23:59:59') < new Date()
                              ? "bg-red-600 hover:bg-red-700 text-white"
                              : "bg-violet-600 hover:bg-violet-700 text-white"
                          }`}
                          data-testid={`reschedule-btn-${claim.id}`}
                        >
                          Reschedule
                        </button>
                      </div>
                    )}

                    {/* Contact Information */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Contact Information</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Email</p>
                      <a href={`mailto:${claim.customer_email}`} className="text-sm font-medium text-blue-700 hover:underline break-all inline-block py-1" data-testid={`email-link-${claim.id}`}>
                        {claim.customer_email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Phone</p>
                      <a href={`tel:${claim.customer_phone}`} className="text-sm font-medium text-blue-700 hover:underline inline-block py-1" data-testid={`phone-link-${claim.id}`}>
                        {claim.customer_phone}
                      </a>
                    </div>
                  </div>
                      </div>
                    </div>

                    {/* Vehicle & Service Information */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Vehicle & Service</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Vehicle Information */}
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <Car className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Vehicle</p>
                            <p className="text-sm font-medium text-gray-900">
                              {claim.vehicle_make && claim.vehicle_model 
                                ? `${claim.vehicle_year ? claim.vehicle_year + " " : ""}${claim.vehicle_make} ${claim.vehicle_model}` 
                                : <span className="text-gray-400 italic">Not provided</span>
                              }
                            </p>
                          </div>
                        </div>
                        
                        {/* Service Address */}
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <MapPin className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Service Address</p>
                            <div className="text-sm font-medium text-gray-900">
                              {claim.street_address ? (
                                <>
                                  <div>{claim.street_address}{claim.apartment_unit && `, ${claim.apartment_unit}`}</div>
                                  <div>{claim.city}, {claim.state} {claim.zip_code}</div>
                                </>
                              ) : claim.service_address ? (
                                <div className="whitespace-pre-line">{claim.service_address}</div>
                              ) : (
                                <span className="text-gray-400 italic">Not provided</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Technical Details */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Technical Details</h4>
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Diagnostic Code */}
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <Code className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Diagnostic Code</p>
                            <p className="text-sm font-medium text-gray-900 font-mono">
                              {claim.diagnostic_code || <span className="text-gray-400 italic">Not provided</span>}
                            </p>
                          </div>
                        </div>
                        
                        {/* Invoice Number */}
                        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <Receipt className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Invoice Number</p>
                            <p className="text-sm font-medium text-gray-900">
                              {claim.invoice_number || <span className="text-gray-400 italic">Not provided</span>}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                {/* Additional Notes */}
                {claim.additional_notes && (
                  <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-start gap-2">
                      <StickyNote className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-amber-900 mb-1">
                          Additional Notes
                        </p>
                        <p className="text-sm text-amber-800 whitespace-pre-line">
                          {claim.additional_notes}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Diagnostic Code Images */}
                {(claim.diagnostic_code_image_paths?.length > 0 || claim.diagnostic_code_image_path) && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-900">
                          {claim.diagnostic_code_image_paths?.length > 0 
                            ? `${claim.diagnostic_code_image_paths.length} diagnostic code image${claim.diagnostic_code_image_paths.length > 1 ? 's' : ''} attached`
                            : 'Diagnostic code image attached'
                          }
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        {/* New claims with multiple images */}
                        {claim.diagnostic_code_image_paths?.map((imagePath, index) => (
                          <Button
                            key={index}
                            onClick={() => handleViewFile(imagePath, "image", `${claim.customer_name} - Image ${index + 1}`)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                            data-testid={`view-diagnostic-image-${claim.id}-${index}`}
                          >
                            <FileText className="w-4 h-4" />
                            View Image {index + 1}
                          </Button>
                        ))}
                        
                        {/* Old claims with single image */}
                        {claim.diagnostic_code_image_path && !claim.diagnostic_code_image_paths && (
                          <Button
                            onClick={() => handleViewFile(claim.diagnostic_code_image_path, "image", claim.customer_name)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
                            data-testid={`view-diagnostic-image-${claim.id}`}
                          >
                            <FileText className="w-4 h-4" />
                            View Image
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {claim.invoice_file_path && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">
                          Invoice document attached
                        </span>
                      </div>
                      <Button
                        onClick={() => handleViewFile(claim.invoice_file_path, "invoice", claim.customer_name)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                        data-testid={`view-invoice-${claim.id}`}
                      >
                        <FileText className="w-4 h-4" />
                        View Invoice
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
                  {/* Update Status */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-700 w-[120px]">
                      Update Status:
                    </span>
                    <Select
                      value={claim.status}
                      onValueChange={(value) => updateClaimStatus(claim.id, value)}
                      disabled={updatingClaim === claim.id}
                    >
                      <SelectTrigger className="w-[220px]" data-testid={`update-status-${claim.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {updatingClaim === claim.id && (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    )}
                  </div>

                  {/* Update Priority */}
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-700 w-[120px]">
                      Update Priority:
                    </span>
                    <Select
                      value={claim.priority || "Normal"}
                      onValueChange={(value) => updateClaimPriority(claim.id, value)}
                      disabled={updatingClaim === claim.id}
                    >
                      <SelectTrigger className="w-[220px]" data-testid={`update-priority-${claim.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((priority) => (
                          <SelectItem key={priority} value={priority}>
                            <div className="flex items-center gap-2">
                              {priority === "Urgent" && (
                                <Flag className="w-4 h-4" />
                              )}
                              {priority}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quick Schedule Button for Info Verified */}
                  {claim.status === "Info Verified" && (
                    <button
                      onClick={() => updateClaimStatus(claim.id, "Scheduled")}
                      className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Calendar className="w-5 h-5" />
                      Schedule Appointment
                    </button>
                  )}

                  {/* Mark as Completed Button for Past Scheduled */}
                  {claim.status === "Scheduled" && claim.scheduled_date && new Date(claim.scheduled_date + 'T23:59:59') < new Date() && (
                    <button
                      onClick={() => updateClaimStatus(claim.id, "Completed")}
                      className="w-full py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />
                      Mark as Completed
                    </button>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <div className="space-y-4 overflow-hidden">
                  {/* Refresh Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={() => {
                        loadClaimMessages(claim.id, true);
                        toast.success("Messages refreshed!");
                      }}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                    >
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Refresh Messages</span>
                      <span className="sm:hidden">Refresh</span>
                    </Button>
                  </div>
                  
                  {/* Messages Display Area */}
                  <div className="border rounded-lg p-2 sm:p-4 bg-gray-50 min-h-[300px] max-h-[400px] overflow-y-auto overflow-x-hidden">
                    {!claimMessages[claim.id] || claimMessages[claim.id]?.isLoading ? (
                      <div className="flex items-center justify-center h-[300px]">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      </div>
                    ) : claimMessages[claim.id]?.messages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                        <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
                        <p>No messages yet</p>
                        <p className="text-sm">Send a message to start the conversation</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {claimMessages[claim.id]?.messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`flex ${msg.sender_type === 'manager' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[85%] sm:max-w-[80%] rounded-lg p-2.5 sm:p-3 ${
                                msg.sender_type === 'manager'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-white border border-gray-200 text-gray-900'
                              }`}
                              style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold opacity-90">
                                  {msg.sender_type === 'manager' ? 'You' : msg.sender_name}
                                </span>
                                <span className="text-xs opacity-70">
                                  {formatDate(msg.created_at)}
                                </span>
                              </div>
                              {msg.message && <p className="text-sm whitespace-pre-line">{msg.message}</p>}
                              {msg.media_urls && msg.media_urls.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {msg.media_urls.map((url, idx) => (
                                    <div
                                      key={idx}
                                      onClick={() => setImageModal({ isOpen: true, imageUrl: url, imageAlt: `Attachment ${idx + 1}` })}
                                      className="cursor-pointer"
                                    >
                                      <img 
                                        src={url} 
                                        alt={`Attachment ${idx + 1}`}
                                        className="max-w-full rounded border border-gray-300 hover:opacity-90 hover:shadow-lg transition-all"
                                        style={{maxHeight: '300px'}}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Reply Input Area */}
                  <div className="space-y-2">
                    <Textarea
                      value={claimMessages[claim.id]?.newMessage || ""}
                      onChange={(e) => {
                        loadClaimMessages(claim.id); // Ensure messages are loaded
                        setClaimMessages(prev => ({
                          ...prev,
                          [claim.id]: {
                            ...prev[claim.id],
                            newMessage: e.target.value
                          }
                        }));
                      }}
                      placeholder="Type your message to the customer..."
                      className="min-h-[100px] resize-none"
                      disabled={claimMessages[claim.id]?.isSending}
                      data-testid={`message-input-${claim.id}`}
                    />
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <p className="text-xs text-gray-500">
                        Message will be sent to customer via SMS
                      </p>
                      <Button
                        onClick={() => sendInlineMessage(claim.id)}
                        disabled={!claimMessages[claim.id]?.newMessage?.trim() || claimMessages[claim.id]?.isSending}
                        className="flex items-center gap-2 w-full sm:w-auto"
                        data-testid={`send-message-button-${claim.id}`}
                      >
                        {claimMessages[claim.id]?.isSending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                        Send Message
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
              </div>
            ))}
            {filteredClaims.length > visibleCount && (
              <div className="flex flex-col items-center gap-2 py-4" data-testid="load-more-wrapper">
                <p className="text-sm text-gray-500">
                  Showing {visibleCount} of {filteredClaims.length} claims
                </p>
                <Button
                  variant="outline"
                  onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                  data-testid="load-more-claims-button"
                >
                  Show {Math.min(PAGE_SIZE, filteredClaims.length - visibleCount)} more
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* File View Modal */}
      <Dialog open={viewModal.isOpen} onOpenChange={closeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-bold">
                {viewModal.title} - {viewModal.claimName}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={openInNewTab}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid="open-in-new-tab"
                >
                  <Maximize2 className="w-4 h-4" />
                  Open in New Tab
                </Button>
                <Button
                  onClick={closeModal}
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid="close-modal"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>
          
          <div className="mt-4">
            {viewModal.filename && (
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                {(() => {
                  const fileExtension = viewModal.filename.split('.').pop().toLowerCase();
                  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension);
                  
                  if (isImage) {
                    return (
                      <img
                        src={`${BACKEND_URL}/api/uploads/${viewModal.filename}`}
                        alt={viewModal.title}
                        className="max-w-full max-h-[60vh] mx-auto rounded-lg shadow-lg"
                        data-testid="modal-image"
                      />
                    );
                  } else {
                    return (
                      <div className="space-y-4" data-testid="modal-document">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto" />
                        <div>
                          <p className="text-lg font-medium text-gray-900 mb-2">
                            {fileExtension.toUpperCase()} Document
                          </p>
                          <p className="text-sm text-gray-600 mb-4">
                            Click "Open in New Tab" to view the full document
                          </p>
                          <Button
                            onClick={openInNewTab}
                            className="flex items-center gap-2 mx-auto"
                            data-testid="view-document-button"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Open Document
                          </Button>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Message Modal */}
      <Dialog open={messageModal.isOpen} onOpenChange={closeMessageModal}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Messages - Claim #{messageModal.claim?.claim_number}
            </DialogTitle>
            <p className="text-sm text-gray-600">
              {messageModal.claim?.customer_name} ({messageModal.claim?.customer_email})
            </p>
          </DialogHeader>

          <div className="flex flex-col h-[500px]">
            {/* Messages Display Area */}
            <div className="flex-1 overflow-y-auto border rounded-lg p-4 mb-4 bg-gray-50">
              {messageModal.isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                </div>
              ) : messageModal.messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <MessageCircle className="w-12 h-12 mb-2 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Send a message to start the conversation</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messageModal.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'manager' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.sender_type === 'manager'
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-200 text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold opacity-90">
                            {msg.sender_type === 'manager' ? 'You' : msg.sender_name}
                          </span>
                          <span className="text-xs opacity-70">
                            {formatDate(msg.created_at)}
                          </span>
                        </div>
                        {msg.message && <p className="text-sm whitespace-pre-line">{msg.message}</p>}
                        {msg.media_urls && msg.media_urls.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {msg.media_urls.map((url, idx) => (
                              <div
                                key={idx}
                                onClick={() => setImageModal({ isOpen: true, imageUrl: url, imageAlt: `Attachment ${idx + 1}` })}
                                className="cursor-pointer"
                              >
                                <img 
                                  src={url} 
                                  alt={`Attachment ${idx + 1}`}
                                  className="max-w-full rounded border border-gray-300 hover:opacity-90 hover:shadow-lg transition-all"
                                  style={{maxHeight: '300px'}}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reply Input Area */}
            <div className="space-y-2">
              <Textarea
                value={messageModal.newMessage}
                onChange={(e) =>
                  setMessageModal((prev) => ({
                    ...prev,
                    newMessage: e.target.value,
                  }))
                }
                placeholder="Type your message to the customer..."
                className="min-h-[100px] resize-none"
                disabled={messageModal.isSending}
                data-testid="message-input"
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Message will be sent to customer via SMS
                </p>
                <Button
                  onClick={sendMessage}
                  disabled={!messageModal.newMessage.trim() || messageModal.isSending}
                  className="flex items-center gap-2"
                  data-testid="send-message-button"
                >
                  {messageModal.isSending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send Message
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog open={statusModal.isOpen} onOpenChange={closeStatusModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Update Status: {statusModal.newStatus}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {statusModal.newStatus === "Missing Information" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What information is missing? *
                </label>
                <Textarea
                  value={statusModal.missingInfoMessage}
                  onChange={(e) =>
                    setStatusModal((prev) => ({
                      ...prev,
                      missingInfoMessage: e.target.value,
                    }))
                  }
                  placeholder="Please provide: diagnostic code images, invoice number, etc."
                  className="min-h-[120px]"
                  data-testid="missing-info-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  This message will be sent to the customer via email and SMS with a link to upload the missing information.
                </p>
              </div>
            )}

            {statusModal.newStatus === "Scheduled" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled Date *
                  </label>
                  <input
                    type="date"
                    value={statusModal.scheduledDate}
                    onChange={(e) =>
                      setStatusModal((prev) => ({
                        ...prev,
                        scheduledDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="scheduled-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Scheduled Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={statusModal.scheduledTime}
                    onChange={(e) =>
                      setStatusModal((prev) => ({
                        ...prev,
                        scheduledTime: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="scheduled-time-input"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Customer will receive an email and SMS with the appointment details and a link to confirm.
                </p>
              </div>
            )}

            {statusModal.newStatus === "Denied" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for denial *
                </label>
                <Textarea
                  value={statusModal.denialReason}
                  onChange={(e) =>
                    setStatusModal((prev) => ({
                      ...prev,
                      denialReason: e.target.value,
                    }))
                  }
                  placeholder="This issue is not covered under warranty because..."
                  className="min-h-[120px]"
                  data-testid="denial-reason-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  This reason will be sent to the customer via email and SMS.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={closeStatusModal}
                disabled={updatingClaim}
              >
                Cancel
              </Button>
              <Button
                onClick={submitStatusUpdate}
                disabled={updatingClaim}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-status-button"
              >
                {updatingClaim ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  `Update to ${statusModal.newStatus}`
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={rescheduleModal.isOpen} onOpenChange={(open) => !open && setRescheduleModal({ isOpen: false, claimId: null, claimNumber: null, currentDate: "", currentTime: "", newDate: "", newTime: "" })}>
        <DialogContent className="max-w-md" data-testid="reschedule-modal">
          <DialogHeader>
            <DialogTitle>Reschedule Appointment</DialogTitle>
            <DialogDescription>
              Claim #{rescheduleModal.claimNumber} — Update the appointment date and time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {rescheduleModal.currentDate && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <p className="text-gray-500 mb-1">Current appointment:</p>
                <p className="font-semibold text-gray-800">
                  {new Date(rescheduleModal.currentDate).toLocaleDateString("en-US", { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })}
                  {rescheduleModal.currentTime && ` at ${rescheduleModal.currentTime}`}
                </p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Date *</label>
              <input
                type="date"
                value={rescheduleModal.newDate}
                onChange={(e) => setRescheduleModal(prev => ({ ...prev, newDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="reschedule-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Time (Optional)</label>
              <input
                type="time"
                value={rescheduleModal.newTime}
                onChange={(e) => setRescheduleModal(prev => ({ ...prev, newTime: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="reschedule-time-input"
              />
            </div>
            <p className="text-xs text-gray-500">Customer will be notified of the new appointment via SMS and email.</p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setRescheduleModal({ isOpen: false, claimId: null, claimNumber: null, currentDate: "", currentTime: "", newDate: "", newTime: "" })}>
                Cancel
              </Button>
              <Button
                onClick={submitReschedule}
                disabled={updatingClaim}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="submit-reschedule-button"
              >
                {updatingClaim ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating...</> : "Reschedule"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox Modal */}
      <Dialog open={imageModal.isOpen} onOpenChange={(open) => !open && setImageModal({ isOpen: false, imageUrl: "", imageAlt: "" })}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden">
          <div className="relative">
            <button
              onClick={() => setImageModal({ isOpen: false, imageUrl: "", imageAlt: "" })}
              className="absolute top-4 right-4 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <img 
              src={imageModal.imageUrl} 
              alt={imageModal.imageAlt}
              className="w-full h-auto max-h-[85vh] object-contain bg-black"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerDashboard;